import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Check, Zap, Crown, Star, Calendar, HelpCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function PlansPage() {
  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold mb-4">Subscription Plans</h1>
        <p className="text-lg text-gray-700 mb-6">
          AISEOBlogGenerator offers multiple subscription tiers to meet different needs and budgets. This guide explains the features and limitations of each plan.
        </p>
      </div>

      <div className="prose max-w-none">
        <h2>Plan Overview</h2>
        <p>
          We offer four tiers of service, each designed for different use cases and content volume needs. All plans include our core AI content generation capabilities, with higher tiers providing more posts per month, access to premium models, and additional features.
        </p>
      </div>

      <div className="bg-white border rounded-lg shadow-md overflow-hidden">
        <div className="px-6 py-4 bg-gray-50 border-b">
          <h3 className="text-xl font-bold">Plan Comparison</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Feature</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="flex items-center justify-center gap-1">
                    <Star className="w-4 h-4 text-gray-400" />
                    <span>Free</span>
                  </div>
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="flex items-center justify-center gap-1">
                    <Zap className="w-4 h-4 text-blue-600" />
                    <span>Pro</span>
                  </div>
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="flex items-center justify-center gap-1">
                    <Crown className="w-4 h-4 text-purple-600" />
                    <span>Enterprise</span>
                  </div>
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="flex items-center justify-center gap-1">
                    <Crown className="w-4 h-4 text-amber-600" />
                    <span>Enterprise VIP</span>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              <tr>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">Monthly Post Limit</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-center">3</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-center">15</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-center">100</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-center">1,000</td>
              </tr>
              <tr>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">Max Words Per Post</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-center">2,000</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-center">4,000</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-center">4,000</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-center">8,000</td>
              </tr>
              <tr>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">Premium AI Models</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-center">Basic Models</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-center">Basic + Pro Models</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-center">All Models</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-center">All Models</td>
              </tr>
              <tr>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">Custom Writing Styles</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-center">✓</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-center">✓</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-center">✓</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-center">✓</td>
              </tr>
              <tr>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">Strike List</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-center">✓</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-center">✓</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-center">✓</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-center">✓</td>
              </tr>
              <tr>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">Marketer Mode</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-center">-</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-center">✓</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-center">✓</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-center">✓</td>
              </tr>
              <tr>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">Priority Support</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-center">-</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-center">-</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-center">✓</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-center">✓</td>
              </tr>
              <tr>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">Monthly Price</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-center">Free</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                  <div>$9.95</div>
                  <div className="text-xs text-gray-400"><s>Regular: $24.95</s></div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                  <div>$49.95</div>
                  <div className="text-xs text-gray-400"><s>Regular: $110</s></div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-center">Contact Sales</td>
              </tr>
              <tr>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">Annual Price (Save 20%)</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-center">Free</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                  <div>$79.95/year</div>
                  <div className="text-xs text-gray-400"><s>Regular: $249.50/year</s></div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                  <div>$379/year</div>
                  <div className="text-xs text-gray-400"><s>Regular: $1,100/year</s></div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-center">Contact Sales</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2 mt-8">
        <div className="bg-white border rounded-lg p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-4">
            <Calendar className="w-5 h-5 text-blue-700" />
            <h3 className="text-lg font-semibold">Monthly Usage & Limits</h3>
          </div>
          
          <p className="text-gray-700 mb-4">
            Each subscription tier includes a specific number of AI-generated posts per month. Here's what you need to know about usage limits:
          </p>
          
          <div className="space-y-3 text-sm text-gray-600">
            <div className="p-3 border rounded-md">
              <strong>Monthly Reset:</strong> Your post count resets on the first day of each calendar month.
            </div>
            
            <div className="p-3 border rounded-md">
              <strong>Usage Tracking:</strong> You can monitor your current usage in the top navigation bar.
            </div>
            
            <div className="p-3 border rounded-md">
              <strong>Unused Posts:</strong> Posts do not roll over to the next month if unused.
            </div>
            
            <div className="p-3 border rounded-md">
              <strong>Post Definition:</strong> Each time you generate a new piece of content, it counts as one post toward your monthly limit.
            </div>
            
            <div className="p-3 border rounded-md">
              <strong>Editing:</strong> Copying a post as a template for a new one will count as an additional post.
            </div>
          </div>
        </div>
        
        <div className="bg-white border rounded-lg p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-4">
            <HelpCircle className="w-5 h-5 text-blue-700" />
            <h3 className="text-lg font-semibold">Frequently Asked Questions</h3>
          </div>
          
          <div className="space-y-4">
            <div className="border-b pb-3">
              <h4 className="font-medium mb-1">Can I upgrade or downgrade my plan?</h4>
              <p className="text-sm text-gray-600">
                Yes, you can change your subscription tier at any time. Changes take effect at the start of your next billing cycle.
              </p>
            </div>
            
            <div className="border-b pb-3">
              <h4 className="font-medium mb-1">What happens if I reach my monthly limit?</h4>
              <p className="text-sm text-gray-600">
                Once you've used all of your monthly posts, you'll need to wait until the next month or upgrade to a higher tier to generate more content.
              </p>
            </div>
            
            <div className="border-b pb-3">
              <h4 className="font-medium mb-1">Is there a refund policy?</h4>
              <p className="text-sm text-gray-600">
                We offer a 7-day money-back guarantee for new subscribers. If you're not satisfied, contact our support team within 7 days of your subscription purchase.
              </p>
            </div>
            
            <div className="border-b pb-3">
              <h4 className="font-medium mb-1">Do you offer team or agency plans?</h4>
              <p className="text-sm text-gray-600">
                Yes, our Enterprise plans can be customized for teams and agencies. Contact our sales team for more information.
              </p>
            </div>
            
            <div>
              <h4 className="font-medium mb-1">How do I cancel my subscription?</h4>
              <p className="text-sm text-gray-600">
                You can cancel your subscription at any time from your account settings. Your subscription will remain active until the end of the current billing period.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-100 rounded-lg p-6 mt-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div>
            <h3 className="text-xl font-bold text-blue-800 mb-2">Founder Launch Pricing</h3>
            <p className="text-blue-700">
              For a limited time, get our premium plans at up to 80% off regular price! These special rates won't last forever.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <Button asChild variant="outline" className="bg-white">
              <Link to="/contact">Contact Sales</Link>
            </Button>
            <Button asChild>
              <Link to="/">View Pricing</Link>
            </Button>
          </div>
        </div>
      </div>

      <div className="flex justify-between mt-8 pt-6 border-t">
        <Button variant="outline" asChild>
          <Link to="/docs/settings">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back: Account Settings
          </Link>
        </Button>
        <Button asChild variant="outline">
          <Link to="/docs">
            Back to Documentation Home
          </Link>
        </Button>
      </div>
    </div>
  );
}